import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';

/**
 * AuthCallback handles Google OAuth callback
 * Processes session_id from URL fragment and exchanges it for a JWT token
 * 
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { loginWithGoogle } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        console.log('AuthCallback: Processing hash:', hash);
        
        const params = new URLSearchParams(hash.replace('#', ''));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          console.error('AuthCallback: No session_id found in URL');
          navigate('/login', { replace: true });
          return;
        }

        console.log('AuthCallback: Exchanging session_id for token...');

        // Exchange session_id for JWT token
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/google/session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId }),
        });

        const data = await response.json();
        console.log('AuthCallback: Response status:', response.status);

        if (!response.ok) {
          console.error('AuthCallback: Auth failed:', data.detail);
          throw new Error(data.detail || 'Authentication failed');
        }
        
        console.log('AuthCallback: Login successful, storing token...');
        
        // Store token and user data
        loginWithGoogle(data.token, data.user);

        // If this is a new user (first time Google login), trigger onboarding tour
        if (data.is_new_user) {
          sessionStorage.setItem('urenbuddy_new_registration', 'true');
        }

        // Clear the URL fragment completely by replacing the entire URL
        const cleanUrl = window.location.origin + window.location.pathname;
        window.history.replaceState(null, '', cleanUrl);
        
        console.log('AuthCallback: Redirecting to dashboard...');
        
        // Force navigation by reloading the page to clear the hash state
        window.location.href = '/dashboard';
      } catch (error) {
        console.error('AuthCallback: Error:', error);
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, [navigate, loginWithGoogle]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto mb-4"></div>
        <p className="text-muted-foreground">Inloggen met Google...</p>
      </div>
    </div>
  );
}
